"use client";

import { curveLinear } from "@visx/curve";
import { LinePath } from "@visx/shape";
import { motion } from "framer-motion";
import React from "react";
import { useChartContext } from "../../../context/ChartContext";
import { BaseDataPoint } from "../../../utils/charts";

interface LineSeriesProps {
  data: BaseDataPoint[];
  color?: string;
  label?: string;
  strokeWidth?: number;
  /** CSS stroke-dasharray value (e.g. "4 2" for dotted, "8 4" for dashed) */
  strokeDasharray?: string;
  categoryColors?: Record<string, string>;
  categoryLabels?: Record<string, string>;
}

/**
 * LineSeries
 *
 * Chart component for rendering line charts.
 * Displays data as a continuous line with customizable stroke width, color, and dash patterns.
 */
export const LineSeries: React.FC<LineSeriesProps> = ({
  data,
  color = "var(--color-viz-default)",
  strokeWidth = 3,
  strokeDasharray,
}) => {
  const { xScale, yScale, animationSettings } = useChartContext();
  const yRange = yScale.range();

  return (
    <motion.g
      initial={{ scaleY: 0 }}
      animate={{ scaleY: 1 }}
      transition={{
        duration: animationSettings.duration,
        ease: animationSettings.ease as "easeInOut",
      }}
      style={{
        transformOrigin: `0 ${yRange[0]}px`,
        transformBox: "fill-box",
        isolation: "isolate",
      }}
    >
      <LinePath
        data={data}
        x={(d) => xScale(d.xValue)}
        y={(d) => yScale(d.yValue)}
        stroke={color}
        strokeWidth={strokeWidth}
        strokeDasharray={strokeDasharray}
        strokeLinecap="round"
        curve={curveLinear}
      />
    </motion.g>
  );
};
